// User roles
export type UserRole = 'admin' | 'agent' | 'customer_manager' | 'customer_viewer';
export type BillingModel = 'hourly' | 'retainer' | 'hour_bank' | 'fixed_price';
export type TicketStatus = 'open' | 'in_progress' | 'waiting' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';
export type WorkLogSource = 'manual' | 'voice_ai' | 'timer';

export interface Organization {
  id: string;
  name: string;
  billing_model: BillingModel;
  hourly_rate: number;
  retainer_hours?: number;
  ticket_visibility: 'own' | 'all';
  active: boolean;
  tax_id?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  website?: string;
  notes?: string;
  sla_response_hours?: number;
  sla_resolution_hours?: number;
  business_hours_start?: string;
  business_hours_end?: string;
  business_days?: number[];
  account_manager_id?: string;
  account_manager_name?: string;
  created_at: string;
  updated_at: string;
}

export type TimerStatus = 'active' | 'suspended';
export type PreferredLanguage = 'he' | 'en';

export interface TicketFilterPreferences {
  assigned: boolean;
  commented: boolean;
  created: boolean;
  include_closed: boolean;
}

export interface User {
  id: string;
  organization_id: string;
  role: UserRole;
  name: string;
  email: string;
  phone?: string;
  active: boolean;
  timer_started_at?: string;
  timer_ticket_id?: string;
  timer_status?: TimerStatus;
  email_notifications: boolean;
  preferred_language: PreferredLanguage;
  ticket_filter_preferences: TicketFilterPreferences;
  dashboard_widget_preferences?: string[];
  created_at: string;
  updated_at: string;
}

export interface TicketAgent {
  id: string;
  name: string;
  role: UserRole;
  assigned_at?: string;
}

export type TaskType = 'ticket' | 'task' | 'discussion';

export interface Ticket {
  id: string;
  organization_id: string;
  customer_id: string;
  contact_id?: string;
  title: string;
  description?: string;
  status: TicketStatus;
  priority: TicketPriority;
  assigned_agent_id?: string;
  project_id?: string;
  milestone_id?: string;
  parent_ticket_id?: string;
  task_type: TaskType;
  estimated_hours?: number;
  actual_hours: number;
  order_index: number;
  billing_model_override?: BillingModel;
  hourly_rate_override?: number;
  customer_summary?: string;
  due_date?: string;
  effective_due_date?: string;
  due_date_source?: 'ticket' | 'milestone' | 'project';
  first_response_at?: string;
  sla_response_due_at?: string;
  sla_resolution_due_at?: string;
  sla_response_breached?: boolean;
  sla_resolution_breached?: boolean;
  ticket_number?: number;
  progress_percent?: number;
  approval_status?: 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_at?: string;
  opened_at: string;
  closed_at?: string;
  created_at: string;
  updated_at: string;
  customer_name?: string;
  agent_name?: string;
  organization_name?: string;
  project_name?: string;
  milestone_name?: string;
  agents?: TicketAgent[];
}

export interface WorkLog {
  id: string;
  ticket_id: string;
  agent_id: string;
  source: WorkLogSource;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  summary: string;
  ai_confidence?: number;
  requires_review: boolean;
  reviewed: boolean;
  reviewed_by?: string;
  reviewed_at?: string;
  billable: boolean;
  project_id?: string;
  organization_id?: string;
  work_category?: WorkCategory;
  participant_count?: number;
  participants?: { user_id: string; name: string }[];
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  ticket_title?: string;
  agent_name?: string;
  organization_name?: string;
}

export type WorkCategory = 'internal' | 'development' | 'service' | 'hour_bank' | 'consultation';

export interface VoiceEntry {
  id: string;
  organization_id: string;
  agent_id: string;
  audio_url: string;
  audio_duration_seconds?: number;
  transcription_text?: string;
  parsed_payload?: any;
  processing_status: 'pending' | 'processing' | 'processed' | 'failed';
  confidence?: number;
  work_log_id?: string;
  error_message?: string;
  created_at: string;
  processed_at?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  organization: Organization;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code: number;
  };
  meta?: {
    limit?: number;
    offset?: number;
    count?: number;
    total?: number;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface CreateWorkLogDto {
  ticket_id: string;
  start_time: string;
  end_time: string;
  summary: string;
  billable?: boolean;
}

export interface CreateTicketDto {
  customer_id: string;
  title: string;
  description?: string;
  priority?: TicketPriority;
  assigned_agent_id?: string;
  task_type?: TaskType;
  contact_id?: string;
  due_date?: string;
  organization_id?: string;
}

export interface DashboardSummary {
  tickets: { total: number; active: number };
  hours: { total_minutes: number; billable_minutes: number };
  organizations: number;
  users: number;
  monthly_revenue: number;
  recent_work_logs: WorkLog[];
}

export interface BillingSummary {
  organization_id: string;
  organization_name: string;
  billing_model: BillingModel;
  hourly_rate: number;
  retainer_hours?: number;
  total_logs: number;
  total_hours: number;
  billable_hours: number;
  total_amount: number;
  ticket_count: number;
}

export interface AuditLog {
  id: string;
  work_log_id: string;
  changed_by: string;
  changed_by_name: string;
  change_type: 'created' | 'updated' | 'deleted';
  old_values: any;
  new_values: any;
  changed_at: string;
  ticket_title: string;
}

// i18n-aware label helpers — pass the `t` function from useTranslation()
export type TFunction = (key: string) => string;

export const getStatusLabel = (status: TicketStatus, t: TFunction): string =>
  t(`status.${status}`);

export const getPriorityLabel = (priority: TicketPriority, t: TFunction): string =>
  t(`priority.${priority}`);

export const getStatusLabels = (t: TFunction): Record<TicketStatus, string> => ({
  open: t('status.open'),
  in_progress: t('status.in_progress'),
  waiting: t('status.waiting'),
  closed: t('status.closed'),
});

export const getPriorityLabels = (t: TFunction): Record<TicketPriority, string> => ({
  low: t('priority.low'),
  medium: t('priority.medium'),
  high: t('priority.high'),
  urgent: t('priority.urgent'),
});

export const getUserRoleLabel = (role: UserRole, t: TFunction): string =>
  t(`userRole.${role}`);

export const getUserRoleLabels = (t: TFunction): Record<UserRole, string> => ({
  admin: t('userRole.admin'),
  agent: t('userRole.agent'),
  customer_manager: t('userRole.customer_manager'),
  customer_viewer: t('userRole.customer_viewer'),
});

// ==========================================
// Ticket Timeline, Comments, Attachments
// ==========================================

export type TicketEventType =
  | 'ticket_created'
  | 'status_changed'
  | 'priority_changed'
  | 'agent_assigned'
  | 'agent_removed'
  | 'comment_added'
  | 'comment_deleted'
  | 'attachment_uploaded'
  | 'attachment_deleted';

export interface TicketAttachment {
  id: string;
  ticket_id: string;
  comment_id?: string;
  uploader_id: string;
  uploader_name?: string;
  original_name: string;
  stored_filename: string;
  mime_type: string;
  file_size_bytes: number;
  url: string;
  created_at: string;
}

export interface TimelineItem {
  id: string;
  item_type: 'event' | 'comment';
  created_at: string;
  // common
  actor_id?: string;
  actor_name?: string;
  actor_role?: UserRole;
  is_internal: boolean;
  is_deleted: boolean;
  // event fields
  event_type?: TicketEventType;
  metadata?: Record<string, any>;
  // comment fields
  comment_id?: string;
  content?: string | null;
  attachments?: TicketAttachment[];
  edited_at?: string;
}

export interface TicketTimeline {
  timeline: TimelineItem[];
  ticket_attachments: TicketAttachment[];
}

export interface CreateCommentDto {
  content: string;
  is_internal?: boolean;
}

// ==========================================
// Project & Milestone Types
// ==========================================

export type ProjectType = 'service' | 'hour_bank' | 'development';
export type ProjectStatus = 'active' | 'on_hold' | 'completed' | 'cancelled';
export type ProjectBillingModel = 'hourly' | 'hour_bank' | 'fixed_price';
export type MilestoneStatus = 'pending' | 'in_progress' | 'completed';
export type RenewalType = 'one_time' | 'recurring';

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  type: ProjectType;
  description?: string;
  customer_summary?: string;
  status: ProjectStatus;
  billing_model?: ProjectBillingModel;
  hourly_rate?: number;
  fixed_price?: number;
  total_budget_hours?: number;
  renewal_type?: RenewalType;
  renewal_day?: number;
  show_hours_to_customer: boolean;
  start_date?: string;
  end_date?: string;
  deadline_warning_days?: number;
  sla_response_hours?: number | null;
  sla_resolution_hours?: number | null;
  actual_hours?: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectStats {
  total_estimated_hours: number;
  total_actual_hours: number;
  budget_hours_remaining: number;
  tickets_total: number;
  tickets_closed: number;
  completion_percentage: number;
  billing_config: BillingConfig;
}

export interface BillingConfig {
  model: ProjectBillingModel;
  rate?: number;
  fixedPrice?: number;
  budgetHours?: number;
  renewalType?: RenewalType;
}

export interface Milestone {
  id: string;
  project_id: string;
  name: string;
  description?: string;
  due_date?: string;
  status: MilestoneStatus;
  order_index: number;
  estimated_hours?: number;
  actual_hours?: number;
  created_at: string;
}

export const getProjectTypeLabel = (type: ProjectType, t: TFunction): string =>
  t(`projectType.${type}`);

export const getProjectTypeLabels = (t: TFunction): Record<ProjectType, string> => ({
  service: t('projectType.service'),
  hour_bank: t('projectType.hour_bank'),
  development: t('projectType.development'),
});

export const getProjectStatusLabel = (status: ProjectStatus, t: TFunction): string =>
  t(`projectStatus.${status}`);

export const getProjectStatusLabels = (t: TFunction): Record<ProjectStatus, string> => ({
  active: t('projectStatus.active'),
  on_hold: t('projectStatus.on_hold'),
  completed: t('projectStatus.completed'),
  cancelled: t('projectStatus.cancelled'),
});

export type DeadlineStatus = 'ok' | 'approaching' | 'overdue';

export const getMilestoneStatusLabel = (status: MilestoneStatus, t: TFunction): string =>
  t(`milestoneStatus.${status}`);

export const getMilestoneStatusLabels = (t: TFunction): Record<MilestoneStatus, string> => ({
  pending: t('milestoneStatus.pending'),
  in_progress: t('milestoneStatus.in_progress'),
  completed: t('milestoneStatus.completed'),
});

// ==========================================
// Notification Types
// ==========================================

export interface Notification {
  id: string;
  user_id: string;
  ticket_id?: string;
  entity_type: string;
  entity_id?: string;
  event_type: string;
  read_at?: string;
  created_at: string;
  ticket_title?: string;
  organization_name?: string;
}

// ==========================================
// Search Types
// ==========================================

export type SearchResultType = 'ticket' | 'project' | 'comment' | 'worklog';

export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  highlight: string;
  breadcrumb: string;
  updated_at: string;
}

export interface SearchResponse {
  results: SearchResult[];
  totals: Record<SearchResultType, number>;
}

// ==========================================
// Report Types
// ==========================================

export interface BurndownDataPoint {
  date: string;
  ideal: number;
  actual: number;
}

export interface BudgetVsActualData {
  budget: number;
  actual: number;
  percentage: number;
  currency_amount: number;
  budget_amount: number;
}

export interface MonthlyRevenuePoint {
  month: string;
  revenue: number;
}

export interface HoursByAgentData {
  agent_id: string;
  agent_name: string;
  total_hours: number;
  billable_hours: number;
}

export interface TicketsByPriorityData {
  priority: string;
  count: number;
}

export interface HoursByTicketData {
  ticket_id: string;
  ticket_title: string;
  total_hours: number;
  agent_name: string;
}

export interface AgentSummaryData {
  total_hours: number;
  billable_hours: number;
  tickets_handled: number;
  tickets_closed: number;
  avg_resolution_hours: number;
}

// ==========================================
// Integration Engine Types (Sprint 2)
// ==========================================

export type ProviderType = 'telephony' | 'email' | 'gmail_addon' | 'custom';
export type SourceType = 'webhook_inbound' | 'email_imap' | 'http_poll' | 'manual_trigger';
export type InboundLogStatus = 'received' | 'processing' | 'processed' | 'blocked' | 'failed';

export type PipelineStepType =
  | 'download_file'
  | 'transcribe_audio'
  | 'ai_extract'
  | 'identify_customer'
  | 'identify_agent'
  | 'check_blocklist'
  | 'apply_template'
  | 'custom_js';

export type TargetType = 'create_ticket' | 'append_comment' | 'create_work_log' | 'send_email';
export type BlocklistMatchType = 'exact' | 'normalized_phone' | 'domain';
export type BlocklistScope = 'global' | 'personal';

export interface IntegrationPreset {
  preset_key: string;
  provider_type: ProviderType;
  source_type: SourceType;
  mappings_count: number;
  steps_count: number;
  targets_count: number;
  steps: PipelineStepType[];
  targets: TargetType[];
}

export type IntegrationApprovalStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected';

export interface IntegrationInstance {
  id: string;
  key: string;
  name: string;
  provider_type: ProviderType;
  preset_key: string | null;
  source_type: SourceType;
  source_config: Record<string, any>;
  enabled: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  approval_status: IntegrationApprovalStatus;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  submitted_at: string | null;
}

export interface IntegrationFieldMapping {
  id: string;
  instance_id: string;
  source_field_key: string;
  target_field_key: string;
  jsonpath_expression: string;
  is_required: boolean;
  default_value: string | null;
  created_at: string;
}

export interface IntegrationPipelineStep {
  id: string;
  instance_id: string;
  step_order: number;
  step_type: PipelineStepType;
  config_json: Record<string, any>;
  enabled: boolean;
  created_at: string;
}

export interface IntegrationTarget {
  id: string;
  instance_id: string;
  target_order: number;
  target_type: TargetType;
  field_bindings: Record<string, any>;
  enabled: boolean;
  created_at: string;
}

export interface IntegrationSecretKey {
  key_name: string;
  created_at: string;
  updated_at: string;
}

export interface StepTrace {
  step_order: number;
  step_type: PipelineStepType;
  status: 'success' | 'skipped' | 'failed';
  input: Record<string, any>;
  output: Record<string, any> | null;
  error: string | null;
  duration_ms: number;
  started_at: string;
}

export interface IntegrationInboundLog {
  id: string;
  instance_id: string | null;
  source_type: string;
  status: InboundLogStatus;
  external_message_id: string | null;
  raw_payload: Record<string, any> | null;
  mapped_payload: Record<string, any> | null;
  pipeline_context: Record<string, any> | null;
  step_traces: StepTrace[];
  result_summary: Record<string, any> | null;
  error_message: string | null;
  duration_ms: number | null;
  received_at: string;
}

export interface BlocklistRule {
  id: string;
  field_name: string;
  match_value: string;
  match_type: BlocklistMatchType;
  scope: BlocklistScope;
  user_id: string | null;
  reason: string | null;
  created_by: string | null;
  created_by_name?: string;
  created_at: string;
}

export interface EmailTemplate {
  id: string;
  template_key: string;
  organization_id: string | null;
  organization_name?: string;
  locale: string;
  name?: string | null;
  description?: string | null;
  category?: string | null;
  merge_schema_key?: string | null;
  subject_template: string;
  body_template: string;
  is_system: boolean;
  is_active: boolean;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailTemplateMergeField {
  path: string;
  label: string;
  label_en?: string;
  description?: string;
  sample: string;
}

export interface EmailTemplateMergeSchema {
  key: string;
  name: string;
  fields: EmailTemplateMergeField[];
}

export interface EmailTemplatePreview {
  template_id: string;
  template_key: string;
  subject: string;
  html: string;
  text: string;
  locale: string;
}

export interface EmailTemplateAsset {
  id: string;
  url: string;
  path: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
}

export interface TestPipelineResponse {
  status: 'success' | 'failed';
  mapped_payload: Record<string, any>;
  step_traces: StepTrace[];
  context: Record<string, any>;
  error?: string;
  duration_ms: number;
}

export const getProviderTypeLabel = (type: ProviderType, t: TFunction): string =>
  t(`providerType.${type}`);

export const getProviderTypeLabels = (t: TFunction): Record<ProviderType, string> => ({
  telephony: t('providerType.telephony'),
  email: t('providerType.email'),
  gmail_addon: t('providerType.gmail_addon'),
  custom: t('providerType.custom'),
});

export const getSourceTypeLabel = (type: SourceType, t: TFunction): string =>
  t(`sourceType.${type}`);

export const getSourceTypeLabels = (t: TFunction): Record<SourceType, string> => ({
  webhook_inbound: t('sourceType.webhook_inbound'),
  email_imap: t('sourceType.email_imap'),
  http_poll: t('sourceType.http_poll'),
  manual_trigger: t('sourceType.manual_trigger'),
});

export const getStepTypeLabel = (type: PipelineStepType, t: TFunction): string =>
  t(`stepType.${type}`);

export const getStepTypeLabels = (t: TFunction): Record<PipelineStepType, string> => ({
  download_file: t('stepType.download_file'),
  transcribe_audio: t('stepType.transcribe_audio'),
  ai_extract: t('stepType.ai_extract'),
  identify_customer: t('stepType.identify_customer'),
  identify_agent: t('stepType.identify_agent'),
  check_blocklist: t('stepType.check_blocklist'),
  apply_template: t('stepType.apply_template'),
  custom_js: t('stepType.custom_js'),
});

export const getTargetTypeLabel = (type: TargetType, t: TFunction): string =>
  t(`targetType.${type}`);

export const getTargetTypeLabels = (t: TFunction): Record<TargetType, string> => ({
  create_ticket: t('targetType.create_ticket'),
  append_comment: t('targetType.append_comment'),
  create_work_log: t('targetType.create_work_log'),
  send_email: t('targetType.send_email'),
});

export const getBlocklistMatchTypeLabel = (type: BlocklistMatchType, t: TFunction): string =>
  t(`blocklistMatchType.${type}`);

export const getBlocklistMatchTypeLabels = (t: TFunction): Record<BlocklistMatchType, string> => ({
  exact: t('blocklistMatchType.exact'),
  normalized_phone: t('blocklistMatchType.normalized_phone'),
  domain: t('blocklistMatchType.domain'),
});

export const getInboundLogStatusLabel = (status: InboundLogStatus, t: TFunction): string =>
  t(`inboundLogStatus.${status}`);

export const getInboundLogStatusLabels = (t: TFunction): Record<InboundLogStatus, string> => ({
  received: t('inboundLogStatus.received'),
  processing: t('inboundLogStatus.processing'),
  processed: t('inboundLogStatus.processed'),
  blocked: t('inboundLogStatus.blocked'),
  failed: t('inboundLogStatus.failed'),
});

// ==========================================
// API Key Types
// ==========================================

export type ApiKeyScopeType = 'global' | 'org' | 'internal';

export interface ApiKeyInfo {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  scope_type: ApiKeyScopeType;
  scoped_org_id?: string;
  organization_id?: string;
  organization_name?: string;
  last_used_at?: string;
  created_at: string;
  revoked_at?: string;
  created_by_name?: string;
  full_key?: string;
}

// ==========================================
// Webhook Types
// ==========================================

export interface WebhookEndpoint {
  id: string;
  organization_id?: string;
  created_by: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string;
  created_by_name?: string;
  organization_name?: string;
}

export interface WebhookDelivery {
  id: string;
  endpoint_id: string;
  event_type: string;
  payload?: Record<string, any>;
  response_status?: number;
  response_body?: string;
  delivered_at?: string;
  next_retry_at?: string;
  attempt_count: number;
  created_at: string;
}

// ==========================================
// System Settings Types
// ==========================================

export interface SystemSetting {
  id: string;
  category: string;
  key: string;
  value: string | null;
  encrypted: boolean;
  updated_at: string;
  updated_by?: string;
}

export interface DeployRecord {
  id: string;
  triggered_by: string;
  triggered_by_name?: string;
  status: 'running' | 'success' | 'failed' | 'rolled_back';
  started_at: string;
  completed_at?: string;
  log?: string;
  version_before?: string;
  version_after?: string;
}

export type SettingsCategory = 'general' | 'smtp' | 'ai' | 'transcription' | 'security' | 'notifications' | 'deployment' | 'attendance';

// ==========================================
// Attendance Types
// ==========================================

export type AttendanceEventType = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
export type AttendanceSource = 'web' | 'mobile' | 'kiosk' | 'admin_fix';
export type AttendanceSummaryStatus = 'auto' | 'confirmed' | 'disputed' | 'adjusted';

export interface AttendanceRecord {
  id: string;
  user_id: string;
  event_type: AttendanceEventType;
  recorded_at: string;
  source: AttendanceSource;
  notes?: string;
  adjustment_reason?: string;
  adjusted_by?: string;
  adjusted_by_name?: string;
  location_type?: string;
  created_at: string;
}

export interface AttendanceDailySummary {
  id: string;
  user_id: string;
  user_name?: string;
  work_date: string;
  total_work_minutes: number;
  total_break_minutes: number;
  regular_minutes: number;
  overtime_125_minutes: number;
  overtime_150_minutes: number;
  status: AttendanceSummaryStatus;
  employee_confirmed_at?: string;
  manager_confirmed_at?: string;
  manager_id?: string;
  notes?: string;
  events?: AttendanceRecord[];
}

export interface AttendancePolicy {
  id: string;
  organization_id?: string;
  name: string;
  business_hours_start: string;
  business_hours_end: string;
  business_days: number[];
  max_daily_hours: number;
  overtime_daily_threshold_minutes: number;
  overtime_weekly_threshold_minutes: number;
  auto_break_deduction_minutes: number;
  round_to_minutes: number;
  is_default: boolean;
  created_at: string;
}

export interface AttendanceStatus {
  status: 'clocked_in' | 'on_break' | 'clocked_out';
  since?: string;
  today_minutes: number;
  today_break_minutes: number;
}

export interface AttendanceVsTicketsRow {
  user_id: string;
  user_name: string;
  attendance_hours: number;
  ticket_hours: number;
  gap_percent: number;
}

export type MeetingStatus = 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
export type RsvpStatus = 'pending' | 'accepted' | 'declined' | 'tentative';

export interface Meeting {
  id: string;
  organization_id?: string;
  title: string;
  description?: string;
  meeting_datetime: string;
  duration_minutes: number;
  meeting_url?: string;
  status: MeetingStatus;
  follow_up_date?: string;
  follow_up_notes?: string;
  organizer_id: string;
  organizer_name?: string;
  organization_name?: string;
  ticket_id?: string;
  project_id?: string;
  milestone_id?: string;
  participants?: MeetingParticipant[];
  created_at: string;
  updated_at: string;
}

export interface MeetingParticipant {
  id: string;
  meeting_id: string;
  contact_id?: string;
  user_id?: string;
  rsvp_status: RsvpStatus;
  user_name?: string;
  contact_name?: string;
}

export interface ChecklistItem {
  id: string;
  ticket_id: string;
  sort_order: number;
  text: string;
  completed: boolean;
  completed_by?: string;
  completed_at?: string;
  created_by?: string;
  created_by_name?: string;
  completed_by_name?: string;
  created_at: string;
}

export interface AttendanceMonthlySummary {
  id: string;
  user_id: string;
  month_year: string;
  total_work_hours: number;
  total_break_hours: number;
  total_work_days: number;
  total_travel_days: number;
  regular_hours: number;
  overtime_125_hours: number;
  overtime_150_hours: number;
  employee_confirmed: boolean;
  employee_confirmed_at?: string;
  manager_confirmed: boolean;
  manager_confirmed_at?: string;
  manager_id?: string;
  logs_sent_for_review: boolean;
  logs_sent_at?: string;
  employee_confirmed_logs: boolean;
  employee_confirmed_logs_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// ==========================================
// Dashboard Builder Types
// ==========================================

export type WidgetType =
  | 'summary_cards'
  | 'billing_summary'
  | 'monthly_trend'
  | 'recent_activity'
  | 'monthly_revenue'
  | 'hours_by_agent'
  | 'tickets_by_priority'
  | 'hours_by_ticket'
  | 'agent_summary'
  | 'sla_compliance'
  | 'attendance_vs_tickets'
  | 'daily_status'
  | 'managed_clients'
  | 'project_health'
  | 'morning_brief'
  | 'client_energy';

export interface ProjectHealth {
  id: string;
  name: string;
  status: string;
  organization_id: string;
  organization_name: string;
  total_budget_hours: number;
  actual_hours: number;
  remaining_hours: number;
  utilization_pct: number;
  open_tickets: number;
  total_tickets: number;
  completed_milestones: number;
  total_milestones: number;
  end_date?: string;
  days_until_deadline?: number;
}

export interface DashboardWidget {
  id: string;
  dashboard_id: string;
  widget_type: WidgetType;
  title?: string;
  layout_x: number;
  layout_y: number;
  layout_w: number;
  layout_h: number;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface Dashboard {
  id: string;
  user_id: string;
  organization_id: string;
  name: string;
  is_default: boolean;
  sort_order: number;
  config: Record<string, any>;
  widgets?: DashboardWidget[];
  created_at: string;
  updated_at: string;
}

export interface WidgetCatalogItem {
  type: WidgetType;
  name_key: string;
  description_key: string;
  default_w: number;
  default_h: number;
  min_w: number;
  min_h: number;
  max_w: number;
  max_h: number;
  roles: UserRole[];
  data_endpoint: string;
  configurable_filters: string[];
}

// ==========================================
// Contact CRM Types
// ==========================================

export type InteractionType = 'call' | 'email' | 'meeting' | 'note';
export type LocationType = 'office' | 'home' | 'client' | 'other';

export interface Contact {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  role_title?: string;
  department?: string;
  notes?: string;
  is_primary: boolean;
  active: boolean;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ContactInteraction {
  id: string;
  contact_id: string;
  interaction_type: InteractionType;
  summary?: string;
  ticket_id?: string;
  ticket_title?: string;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
}

export const getTaskTypeLabel = (type: TaskType, t: TFunction): string =>
  t(`taskType.${type}`);

export const getTaskTypeLabels = (t: TFunction): Record<TaskType, string> => ({
  ticket: t('taskType.ticket'),
  task: t('taskType.task'),
  discussion: t('taskType.discussion'),
});

export const getLocationTypeLabel = (type: LocationType, t: TFunction): string =>
  t(`locationType.${type}`);

export const getLocationTypeLabels = (t: TFunction): Record<LocationType, string> => ({
  office: t('locationType.office'),
  home: t('locationType.home'),
  client: t('locationType.client'),
  other: t('locationType.other'),
});

export const getInteractionTypeLabel = (type: InteractionType, t: TFunction): string =>
  t(`interactionType.${type}`);

export const getInteractionTypeLabels = (t: TFunction): Record<InteractionType, string> => ({
  call: t('interactionType.call'),
  email: t('interactionType.email'),
  meeting: t('interactionType.meeting'),
  note: t('interactionType.note'),
});

// ==========================================
// Lead Pipeline Types
// ==========================================

export type LeadStatus = 'new' | 'contacted' | 'meeting_scheduled' | 'qualified' | 'proposal_sent' | 'converted' | 'lost';
export type LeadSource = 'facebook' | 'instagram' | 'linkedin' | 'google' | 'website' | 'referral' | 'phone' | 'email' | 'other';
export type CallStatus = 'answered' | 'no_answer' | 'callback' | 'meeting' | 'email_sent' | 'whatsapp';

export interface Lead {
  id: string;
  company_name: string;
  contact_name: string | null;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  address: Record<string, any>;
  lead_quality: number;
  lead_status: LeadStatus;
  lead_source: LeadSource | null;
  loss_reason: string | null;
  total_deal_value: number;
  converted_to_org_id: string | null;
  converted_at: string | null;
  assigned_to: string | null;
  assigned_to_name?: string;
  next_follow_up: string | null;
  notes: string | null;
  created_by: string | null;
  created_by_name?: string;
  products?: LeadProduct[];
  communications?: LeadCommunication[];
  created_at: string;
  updated_at: string;
}

export interface LeadProduct {
  id: string;
  lead_id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  vat_rate: number;
  vat_amount: number;
  notes: string | null;
  created_at: string;
}

export interface LeadCommunication {
  id: string;
  lead_id: string;
  communication_date: string;
  call_status: CallStatus;
  next_communication: string | null;
  summary: string | null;
  created_by: string | null;
  created_by_name?: string;
  created_at: string;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  sku: string | null;
  unit_price: number;
  category: string | null;
  vat_rate: number;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type QuoteType = 'development' | 'service' | 'hour_bank' | 'maintenance' | 'custom';
export type QuoteStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'converted';

export interface Quote {
  id: string;
  organization_id: string | null;
  organization_name?: string;
  lead_id: string | null;
  lead_company_name?: string;
  project_id: string | null;
  quote_number: number;
  quote_type: QuoteType;
  title: string;
  description: string | null;
  status: QuoteStatus;
  total_amount: number;
  vat_total: number;
  grand_total: number;
  valid_until: string | null;
  sent_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejection_reason: string | null;
  document_url: string | null;
  notes: string | null;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  line_items?: QuoteLineItem[];
}

export interface QuoteLineItem {
  id: string;
  quote_id: string;
  sort_order: number;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
  vat_rate: number;
  vat_amount: number;
  notes: string | null;
  created_at: string;
}

export const getLeadStatusLabel = (status: LeadStatus, t: TFunction): string =>
  t(`leadStatus.${status}`);

export const getLeadStatusLabels = (t: TFunction): Record<LeadStatus, string> => ({
  new: t('leadStatus.new'),
  contacted: t('leadStatus.contacted'),
  meeting_scheduled: t('leadStatus.meeting_scheduled'),
  qualified: t('leadStatus.qualified'),
  proposal_sent: t('leadStatus.proposal_sent'),
  converted: t('leadStatus.converted'),
  lost: t('leadStatus.lost'),
});

export const getLeadSourceLabel = (source: LeadSource, t: TFunction): string =>
  t(`leadSource.${source}`);

export const getLeadSourceLabels = (t: TFunction): Record<LeadSource, string> => ({
  facebook: t('leadSource.facebook'),
  instagram: t('leadSource.instagram'),
  linkedin: t('leadSource.linkedin'),
  google: t('leadSource.google'),
  website: t('leadSource.website'),
  referral: t('leadSource.referral'),
  phone: t('leadSource.phone'),
  email: t('leadSource.email'),
  other: t('leadSource.other'),
});

export const getCallStatusLabel = (status: CallStatus, t: TFunction): string =>
  t(`callStatus.${status}`);

export const getCallStatusLabels = (t: TFunction): Record<CallStatus, string> => ({
  answered: t('callStatus.answered'),
  no_answer: t('callStatus.no_answer'),
  callback: t('callStatus.callback'),
  meeting: t('callStatus.meeting'),
  email_sent: t('callStatus.email_sent'),
  whatsapp: t('callStatus.whatsapp'),
});

export const getQuoteTypeLabel = (type: QuoteType, t: TFunction): string =>
  t(`quoteType.${type}`);

export const getQuoteTypeLabels = (t: TFunction): Record<QuoteType, string> => ({
  development: t('quoteType.development'),
  service: t('quoteType.service'),
  hour_bank: t('quoteType.hour_bank'),
  maintenance: t('quoteType.maintenance'),
  custom: t('quoteType.custom'),
});

export const getQuoteStatusLabel = (status: QuoteStatus, t: TFunction): string =>
  t(`quoteStatus.${status}`);

export const getQuoteStatusLabels = (t: TFunction): Record<QuoteStatus, string> => ({
  draft: t('quoteStatus.draft'),
  sent: t('quoteStatus.sent'),
  approved: t('quoteStatus.approved'),
  rejected: t('quoteStatus.rejected'),
  expired: t('quoteStatus.expired'),
  converted: t('quoteStatus.converted'),
});

// ==========================================
// Backup & Restore Types
// ==========================================

export type BackupStatus = 'running' | 'success' | 'failed' | 'partial' | 'verifying' | 'uploading';
export type BackupTriggerType = 'scheduled' | 'manual' | 'pre_restore';
export type StorageProviderType = 'local' | 's3' | 'google_drive';

export interface BackupRecord {
  id: string;
  schedule_id?: string;
  schedule_name?: string;
  storage_config_id?: string;
  storage_name?: string;
  status: BackupStatus;
  trigger_type: BackupTriggerType;
  triggered_by?: string;
  triggered_by_name?: string;
  include_database: boolean;
  include_attachments: boolean;
  include_audio: boolean;
  include_config: boolean;
  include_logs: boolean;
  local_path?: string;
  remote_path?: string;
  remote_provider?: StorageProviderType;
  database_size_bytes?: number;
  attachments_size_bytes?: number;
  audio_size_bytes?: number;
  config_size_bytes?: number;
  logs_size_bytes?: number;
  total_size_bytes?: number;
  database_hash?: string;
  verified_at?: string;
  verified_restorable?: boolean;
  encrypted: boolean;
  database_version?: string;
  table_count?: number;
  row_counts?: Record<string, number>;
  error_message?: string;
  log?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  created_at: string;
}

export interface BackupSchedule {
  id: string;
  name: string;
  cron_expression: string;
  storage_config_id?: string;
  storage_name?: string;
  storage_provider?: StorageProviderType;
  include_database: boolean;
  include_attachments: boolean;
  include_audio: boolean;
  include_config: boolean;
  include_logs: boolean;
  encryption_enabled: boolean;
  retention_days: number;
  max_backups: number;
  active: boolean;
  last_run_at?: string;
  next_run_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BackupStorageConfig {
  id: string;
  name: string;
  provider: StorageProviderType;
  config: Record<string, any>;
  is_default: boolean;
  active: boolean;
  last_tested_at?: string;
  last_test_success?: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BackupStats {
  successful_count: number;
  failed_count: number;
  running_count: number;
  last_successful_backup?: string;
  total_storage_used: number;
  avg_duration_seconds?: number;
  active_schedules: number;
}

export const getStorageProviderLabel = (provider: StorageProviderType, t: TFunction): string =>
  t(`backups.provider_${provider}`);

export const getBackupStatusLabel = (status: BackupStatus, t: TFunction): string =>
  t(`backups.status_${status}`);

export const getBackupTriggerLabel = (trigger: BackupTriggerType, t: TFunction): string =>
  t(`backups.trigger_${trigger}`);
